import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";

import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Paintbrush, Gem, Cake, PartyPopper, ShoppingBag, Candy, Home, Package, PawPrint } from "lucide-react";
import { getProductThumbnail } from "@/lib/imageUtils";
import { rankProducts, type RankStats } from "@/lib/homepageRanking";
import type { LucideIcon } from "lucide-react";

// Map icon names from DB to Lucide components
const iconMap: Record<string, LucideIcon> = {
  Paintbrush,
  gem: Gem,
  Gem,
  ToyBrick: Package,
  Cake,
  PartyPopper,
  ShoppingBag,
  Candy,
  Home,
  PawPrint,
};

interface VisibilityInfo {
  priority_weight: number;
  badge_text: string | null;
  badge_color: string | null;
  type: string;
}

interface CategoryWithProducts {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  productCount: number;
  products: Product[];
}

interface Product {
  id: string;
  title: string;
  price: number;
  images: string[];
  is_digital: boolean;
  is_customizable: boolean;
  created_at: string;
  seller_id: string;
  discount_price: number | null;
  discount_start_date: string | null;
  discount_end_date: string | null;
  is_print_on_demand: boolean;
  print_areas: { mockups?: { placement?: string; url?: string }[] } | null;
  average_rating: number | null;
  review_count: number | null;
  store_name: string | null;
  store_logo: string | null;
  store_whatsapp: string | null;
  visibility: VisibilityInfo | null;
}

const fetchCategoryProducts = async (): Promise<CategoryWithProducts[]> => {
  // 1. Get categories with product counts
  const { data: categories, error: catError } = await supabase
    .from("categories")
    .select("id, name, slug, icon");

  if (catError || !categories) return [];

  // 2. Get all active non-sample products with category
  const { data: productsData, error: prodError } = await supabase
    .from("products")
    .select("id, title, price, images, is_digital, is_customizable, created_at, seller_id, discount_price, discount_start_date, discount_end_date, is_print_on_demand, print_areas, average_rating, review_count, category_id")
    .eq("status", "active")
    .eq("is_sample", false)
    .not("category_id", "is", null);

  if (prodError || !productsData || productsData.length === 0) return [];

  // 3. Get visibility subscriptions
  const { data: subscriptions } = await supabase
    .from("visibility_subscriptions")
    .select(`product_id, seller_id, product_ids, visibility_plans(priority_weight, badge_text, badge_color, type)`)
    .eq("status", "active")
    .gt("expires_at", new Date().toISOString());

  const productPriorityMap = new Map<string, VisibilityInfo>();
  const storePriorityMap = new Map<string, VisibilityInfo>();

  subscriptions?.forEach(sub => {
    const plan = sub.visibility_plans as VisibilityInfo | null;
    if (!plan) return;
    
    // Read from product_ids array (multi-product support)
    const productIds = Array.isArray((sub as any).product_ids) ? (sub as any).product_ids as string[] : [];
    
    if (productIds.length > 0) {
      productIds.forEach(pid => {
        if (pid) productPriorityMap.set(pid, plan);
      });
    } else if (sub.product_id) {
      productPriorityMap.set(sub.product_id, plan);
    } else if (plan.type === 'store') {
      storePriorityMap.set(sub.seller_id, plan);
    }
  });

  // 4. Get seller info (only active membership sellers from public view)
  const sellerIds = [...new Set(productsData.map(p => p.seller_id))];
  const { data: sellersData } = await supabase
    .from("public_seller_stores")
    .select("user_id, store_name, store_logo, store_whatsapp")
    .in("user_id", sellerIds);

  // Only keep products from active sellers
  const activeSellerIds = new Set((sellersData || []).map(s => s.user_id));

  const sellersMap = new Map(
    (sellersData || []).map(s => [s.user_id, { store_name: s.store_name, store_logo: s.store_logo, store_whatsapp: (s as any).store_whatsapp ?? null }])
  );

  // Popularity stats (sales 90d, favorites)
  const { data: statsData } = await supabase.rpc("get_homepage_product_scores");
  const statsMap = new Map<string, RankStats>(
    ((statsData as any[]) || []).map((s) => [s.product_id, { sales_90d: Number(s.sales_90d) || 0, favorites_count: Number(s.favorites_count) || 0 }])
  );

  // 5. Group products by category
  const categoryProductMap = new Map<string, Product[]>();

  productsData.forEach(item => {
    if (!item.category_id) return;
    if (!activeSellerIds.has(item.seller_id)) return;
    const visibility = productPriorityMap.get(item.id) || storePriorityMap.get(item.seller_id) || null;
    const product: Product = {
      id: item.id,
      title: item.title,
      price: item.price,
      images: item.images || [],
      is_digital: item.is_digital || false,
      is_customizable: item.is_customizable || false,
      created_at: item.created_at,
      seller_id: item.seller_id,
      discount_price: item.discount_price,
      discount_start_date: item.discount_start_date,
      discount_end_date: item.discount_end_date,
      is_print_on_demand: item.is_print_on_demand || false,
      print_areas: item.print_areas as Product["print_areas"],
      average_rating: item.average_rating ?? 0,
      review_count: item.review_count ?? 0,
      store_name: sellersMap.get(item.seller_id)?.store_name || null,
      store_logo: sellersMap.get(item.seller_id)?.store_logo || null,
      store_whatsapp: sellersMap.get(item.seller_id)?.store_whatsapp || null,
      visibility,
    };

    const existing = categoryProductMap.get(item.category_id) || [];
    existing.push(product);
    categoryProductMap.set(item.category_id, existing);
  });

  // 6. Build result sorted by product count, apply visibility sorting per category
  const result: CategoryWithProducts[] = categories
    .map(cat => {
      const products = categoryProductMap.get(cat.id) || [];
      if (products.length === 0) return null;

      const sorted = rankProducts(products, statsMap, 8, 2);

      return {
        id: cat.id,
        name: cat.name,
        slug: cat.slug,
        icon: cat.icon,
        productCount: products.length,
        products: sorted,
      } as CategoryWithProducts;
    })
    .filter((c): c is CategoryWithProducts => c !== null)
    .sort((a, b) => b.productCount - a.productCount);

  return result;
};

const CategoryRow = ({ category, rowIndex }: { category: CategoryWithProducts; rowIndex: number }) => {
  const IconComponent = (category.icon && iconMap[category.icon]) || Package;

  return (
    <section className="py-5 md:py-7">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex justify-between items-end mb-4">
          <div className="flex items-center gap-2.5">
            <IconComponent className="h-5 w-5 text-primary" />
            <h2 className="font-display text-xl sm:text-2xl md:text-3xl text-foreground leading-tight">{category.name}</h2>
          </div>
          <Link
            to={`/categoria/${category.slug}`}
            className="text-sm font-semibold text-foreground underline underline-offset-4 decoration-primary/60 hover:decoration-primary flex items-center gap-1 group whitespace-nowrap"
          >
            Ver todo
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        {/* Horizontal scroll on mobile, grid on desktop */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {category.products.map((product, index) => (
            <div key={product.id} className={index >= 6 ? "hidden" : index >= 4 ? "hidden sm:block lg:hidden xl:block" : ""}>
              <ProductCard
                id={product.id}
                title={product.title}
                price={product.price}
                images={product.images}
                isDigital={product.is_digital}
                isCustomizable={product.is_customizable}
                createdAt={product.created_at}
                sellerId={product.seller_id}
                storeName={product.store_name || undefined}
                storeLogo={product.store_logo}
                storeWhatsapp={product.store_whatsapp}
                discountPrice={product.discount_price}
                discountStartDate={product.discount_start_date}
                discountEndDate={product.discount_end_date}
                isPrintOnDemand={product.is_print_on_demand}
                printAreas={product.print_areas}
                rating={product.average_rating || 0}
                reviewCount={product.review_count || 0}
                visibilityBadge={product.visibility?.badge_text || null}
                visibilityBadgeColor={product.visibility?.badge_color || null}
                variant="compact"
                eager={rowIndex < 2 && index < 4}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CategoryProductRows = () => {
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['category-product-rows'],
    queryFn: fetchCategoryProducts,
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <div className="py-4">
        <div className="container mx-auto px-4 space-y-8">
          {[1, 2, 3].map(i => (
            <div key={i}>
              <Skeleton className="h-7 w-48 mb-3" />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map(j => (
                  <Skeleton key={j} className="h-48 rounded-lg" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (categories.length === 0) return null;

  // Cortar la lista hasta e incluyendo "Cerámica" para no alargar demasiado el home
  const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const ceramicaIdx = categories.findIndex(c => normalize(c.name) === 'ceramica');
  const visibleCategories = ceramicaIdx >= 0 ? categories.slice(0, ceramicaIdx + 1) : categories;

  return (
    <div className="bg-background">
      {visibleCategories.map((category, idx) => (
        <CategoryRow key={category.id} category={category} rowIndex={idx} />
      ))}
    </div>
  );

};

export default CategoryProductRows;
